import { Suspense, lazy, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import ForgotPasswordPage from './features/auth/containers/ForgotPasswordPage';
import ResetPasswordPage from './features/auth/containers/ResetPasswordPage';
import Layout from './hoc/Layout';
// import ProtectedRoutes from './hoc/ProtectedRoutes';
import { setCurrentUserflow } from './features/navigation/slices/navigationReducer';
import { fetchCurrentUser, getAllUserInfoByEmail } from './features/auth/actions/authActions';
import { logout } from './features/auth/slices/authSlice';
import { fetchFeaturedLists } from './features/curatedLists/actions/curatedListsActions';
import {
  selectFeaturedListsLastFetched,
  selectIsFetchingFeaturedLists,
} from './features/curatedLists/slices/curatedListsSlice';
import { AppDispatch } from './store/store';
import './scss/index.scss';

import LoadAboutPage from './containers/AboutPage';
import MyReadsManagerComponent from './features/myReads/components/MyReadsManagerComponent';
import NotificationsManagerComponent from './features/notifications/components/NotificationsManagerComponent';
import SiteBannerBar from './components/UIComponents/SiteBannerBar';
import {
  AdminRoute,
  GuestRoute,
  ProfileCreationRoute,
  ProtectedRoute,
} from './hoc/ProtectedRoutes';

const LoadHomePage = lazy(() => import('./containers/HomePage'));

const LoadLoginOrSignUpPage = lazy(
  () => import('./features/auth/containers/LoginOrSignUpPage')
);
const LoadLoginPage = lazy(
  () => import('./features/auth/containers/LoginPage')
);
const LoadSearchResultsPage = lazy(
  () => import('./features/search/SearchResultsPage')
);
const LoadSignUpPage = lazy(
  () => import('./features/auth/containers/SignUpPage')
);

const LoadDonationPage = lazy(
  () => import('./features/donations/containers/DonatePage')
);

const LoadSubscriptionSuccessPage = lazy(
  () => import('./features/donations/containers/SubscriptionSuccessPage')
);

const LoadSubscriptionCanceledPage = lazy(
  () => import('./features/donations/containers/SubscriptionCanceledPage')
);

const LoadBlogPage = lazy(() => import('./features/blogs/containers/BlogPage'));

const LoadFeaturedArticlePage = lazy(
  () => import('./features/featuredArticles/containers/FeaturedArticlePage')
);

const LoadSeeAllBlogsPage = lazy(
  () => import('./features/blogs/containers/SeeAllBlogsPage')
);

const LoadTerrainAdminPage = lazy(
  () => import('./features/terrain/containers/TerrainAdminPage')
);

const LoadClearingsManagerPage = lazy(
  () => import('./features/admin/containers/ClearingsManagerPage')
);

const LoadClearingEditPage = lazy(
  () => import('./features/admin/containers/ClearingEditPage')
);

const LoadUploadEditBlogPage = lazy(
  () => import('./features/blogs/containers/UploadEditBlogPage')
);

const LoadReviewBlogPage = lazy(
  () => import('./features/blogs/containers/ReviewBlogPage')
);

const LoadDiscussionDetailsPage = lazy(
  () => import('./features/discussions/containers/DiscussionDetailsPage')
);

const LoadCreateProfilePage = lazy(
  () => import('./features/profile/containers/CreateProfilePage')
);

const LoadProfilePage = lazy(
  () => import('./features/profile/containers/ProfilePage')
);

const LoadBookmarkedBlogsPage = lazy(
  () => import('./features/profile/containers/BookmarkedBlogsPage')
);

const LoadEditProfilePage = lazy(
  () => import('./features/profile/containers/EditProfilePage')
);

const LoadPublicProfilePage = lazy(
  () => import('./features/publicProfile/containers/PublicProfilePage')
);

const LoadContactForm = lazy(
  () => import('./features/feedback/containers/ContactForm')
);

const LoadSuggestArticlePage = lazy(
  () => import('./features/featuredArticles/containers/SuggestArticlePage')
);

const LoadBlogReviewForm = lazy(
  () => import('./features/blogs/containers/BlogReviewForm')
);

const LoadAdminDashboardPage = lazy(
  () => import('./features/admin/containers/AdminDashboardPage')
);

const LoadNewsFeedPage = lazy(
  () => import('./features/blogs/containers/NewsFeedPage')
);

const LoadBeingDiscussedPage = lazy(
  () => import('./features/discussions/containers/BeingDiscussedPage')
);

const LoadNotificationsComponent = lazy(
  () => import('./features/notifications/containers/NotificationsPage')
);

const LoadMyReadsPage = lazy(
  () => import('./features/myReads/containers/MyReadsPage')
);

const LoadAdminModeratorBlogListPage = lazy(
  () => import('./features/admin/containers/AdminModeratorBlogListPage')
);

const LoadAdminRecentBlogsPage = lazy(
  () => import('./features/admin/containers/AdminRecentBlogsPage')
);

const LoadAdminModeratorBlogDetailPage = lazy(
  () => import('./features/admin/containers/AdminModeratorBlogDetailPage')
);

const LoadFeaturedArticleManagerPage = lazy(
  () => import('./features/admin/containers/FeaturedArticleManagerPage')
);

const LoadAdminCommentModerationPage = lazy(
  () => import('./features/admin/containers/AdminCommentModerationPage')
);

const LoadAdminContactUsPage = lazy(
  () => import('./features/admin/containers/AdminContactUsPage')
);

const LoadAdminArticleSuggestionsPage = lazy(
  () => import('./features/admin/containers/AdminArticleSuggestionsPage')
);

const LoadSiteBannerManagerPage = lazy(
  () => import('./features/admin/containers/SiteBannerManagerPage')
);

const LoadAdminBannedUsersPage = lazy(
  () => import('./features/admin/containers/AdminBannedUsersPage')
);

const LoadAdminEmailSignupSettingsPage = lazy(
  () => import('./features/admin/containers/AdminEmailSignupSettingsPage')
);

const LoadCreateCuratedListPage = lazy(
  () => import('./features/curatedLists/containers/CreateCuratedListPage')
);

const LoadViewCuratedListPage = lazy(
  () => import('./features/curatedLists/containers/viewCuratedListPage')
);

const LoadBrowseCuratedListPage = lazy(
  () => import('./features/curatedLists/containers/browseCuratedListsPage')
);

const LoadBrowseAllListsPage = lazy(
  () => import('./features/curatedLists/containers/browseAllListsPage')
);

const LoadClearingPage = lazy(
  () => import('./features/terrain/containers/ClearingPage')
);

const LoadClearingDumpPage = lazy(
  () => import('./features/terrain/containers/ClearingDumpPage')
);

const fallbackLoader = (
  <div className="center-loader">
    <div className="duck-loader" />
  </div>
);

const FEATURED_LISTS_TTL = 60 * 1000;

const App = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const isLoggedIn = useSelector((state: AppState) => state.user.isLoggedIn);
  const isStaff = useSelector((state: AppState) => state.user.isStaff);
  const authToken = useSelector((state: AppState) => state.user.authToken);
  const hasProfile = useSelector((state: AppState) => state.profile.hasProfile);
  const isNewUser = useSelector((state: AppState) => state.user.isNewUser);
  const featuredListsLastFetched = useSelector(selectFeaturedListsLastFetched);
  const isFetchingFeaturedLists = useSelector(selectIsFetchingFeaturedLists);

  // Verify the stored token is still valid on every app load. If the server
  // rejects it (401), clear Redux auth state so the user is redirected to login
  // rather than seeing a broken admin panel with stale credentials.
  useEffect(() => {
    if (!isLoggedIn || !authToken) return;
    dispatch(fetchCurrentUser(authToken) as any).then((result: any) => {
      if (fetchCurrentUser.rejected.match(result)) {
        dispatch(logout());
      } else if (result.payload?.email) {
        // Refresh the user's own profile (avatar, bio, interests).
        dispatch(getAllUserInfoByEmail(result.payload.email) as any);
      }
    });
  }, []);

  // Prefetch featured lists on app mount so they're ready before HomePage renders.
  // Uses the same 15-min TTL as the backend cache.
  useEffect(() => {
    const stale =
      !featuredListsLastFetched ||
      Date.now() - featuredListsLastFetched > FEATURED_LISTS_TTL;
    if (stale && !isFetchingFeaturedLists) {
      dispatch(fetchFeaturedLists());
    }
  }, [dispatch, featuredListsLastFetched, isFetchingFeaturedLists]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    switch (location.pathname) {
      case '/forgot-password':
      case '/login-or-signup':
      case '/login':
      case '/sign-up':
      case '/create-profile':
        dispatch(setCurrentUserflow('auth-flow'));
        break;
      default:
        dispatch(setCurrentUserflow('home-flow'));
        break;
    }
    if (location.pathname.includes('/password-reset/')) {
      dispatch(setCurrentUserflow('auth-flow'));
    }
  }, [dispatch, location]);

  return (
    <>
      <SiteBannerBar />
      <NotificationsManagerComponent />
      <MyReadsManagerComponent />
      <Layout>
        <Suspense fallback={fallbackLoader}>
          <Routes>
            {/* ============================================================== */}
            {/* AUTHENTICATION CHECK (User must be logged in)         */}
            {/* ============================================================== */}
            <Route element={<ProtectedRoute isAuthenticated={isLoggedIn} />}>
              <Route path="/edit-profile" Component={LoadEditProfilePage} />
              <Route path="/contact-us" Component={LoadContactForm} />
              <Route path="/suggest-article" Component={LoadSuggestArticlePage} />
              <Route path="/upload-blog" Component={LoadUploadEditBlogPage} />
              <Route path="/edit-blog" Component={LoadUploadEditBlogPage} />
              <Route
                path="/review-blog-upsert"
                Component={LoadReviewBlogPage}
              />
              <Route path="/profile" Component={LoadProfilePage} />
              <Route path="/profile/bookmarks" Component={LoadBookmarkedBlogsPage} />
              <Route
                path="/review-blog/:blog_slug/:blog_id"
                Component={LoadBlogReviewForm}
              />
              <Route
                path="/notifications"
                Component={LoadNotificationsComponent}
              />
              <Route
                path="/create-curated-list"
                Component={LoadCreateCuratedListPage}
              />
                     <Route
              path="/user/:username/curated-lists/:slug/edit"
              Component={LoadCreateCuratedListPage}
            />
            </Route>

            {/* ========================================================== */}
            {/* ADMIN ROUTES (User is logged in AND is Staff)       */}
            {/* ========================================================== */}
            <Route
              element={
                <AdminRoute isStaff={isStaff} isAuthenticated={isLoggedIn} />
              }
            >
              {/* Admin Dashboard */}
              <Route path="/admin-wn" Component={LoadAdminDashboardPage} />

              {/* Featured Article Management */}
              <Route
                path="/admin/articles"
                Component={LoadFeaturedArticleManagerPage}
              />
              <Route
                path="/upload-article"
                Component={LoadUploadEditBlogPage}
              />
              <Route path="/edit-article" Component={LoadUploadEditBlogPage} />
              <Route
                path="/review-article-upsert"
                Component={LoadReviewBlogPage}
              />

              {/* Blog Moderation */}
              <Route
                path="/admin/blogs"
                Component={LoadAdminModeratorBlogListPage}
              />
              <Route
                path="/admin/blogs/:blogId"
                Component={LoadAdminModeratorBlogDetailPage}
              />
              <Route
                path="/admin/recent-blogs"
                Component={LoadAdminRecentBlogsPage}
              />
              <Route
                path="/admin/comments"
                Component={LoadAdminCommentModerationPage}
              />
              <Route
                path="/admin/contact-us"
                Component={LoadAdminContactUsPage}
              />
              <Route
                path="/admin/banned-users"
                Component={LoadAdminBannedUsersPage}
              />
              <Route
                path="/admin/suggested-articles"
                Component={LoadAdminArticleSuggestionsPage}
              />
              <Route path="/admin/terrain" Component={LoadTerrainAdminPage} />
              <Route path="/admin/clearings" Component={LoadClearingsManagerPage} />
              <Route path="/admin/clearings/:slug" Component={LoadClearingEditPage} />
              <Route path="/admin/site-banner" Component={LoadSiteBannerManagerPage} />
              <Route path="/admin/email-signup" Component={LoadAdminEmailSignupSettingsPage} />
            </Route>

            {/* ========================================================== */}
            {/* GUEST ROUTES (User must NOT be logged in)                  */}
            {/* ========================================================== */}
            <Route
              element={
                <GuestRoute
                  isAuthenticated={isLoggedIn}
                  hasProfile={hasProfile}
                  isNewUser={isNewUser}
                  redirectPath="/"
                />
              }
            >
              <Route
                path="/login-or-signup"
                Component={LoadLoginOrSignUpPage}
              />
              <Route path="/sign-up" Component={LoadSignUpPage} />
              <Route path="/login" Component={LoadLoginPage} />
              <Route path="/forgot-password" Component={ForgotPasswordPage} />
            </Route>

            {/* ========================================================== */}
            {/* PROFILE CREATION ROUTE - New users without profile         */}
            {/* ========================================================== */}

            <Route
              element={
                <ProfileCreationRoute
                  isAuthenticated={isLoggedIn}
                  hasProfile={hasProfile}
                  isNewUser={isNewUser}
                />
              }
            >
              <Route path="/create-profile" Component={LoadCreateProfilePage} />
            </Route>

            {/* ========================================================== */}
            {/* PUBLIC ROUTES - No authentication required                 */}
            {/* ========================================================== */}
            <Route path="/" Component={LoadHomePage} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/conversations" Component={LoadNewsFeedPage} />
            <Route path="/discussions" Component={LoadBeingDiscussedPage} />
            <Route path="/my-reads" Component={LoadMyReadsPage} />
            <Route path="/newsfeed" element={<Navigate to="/conversations" replace />} />
            <Route path="/about" Component={LoadAboutPage} />
            <Route path="/donate" Component={LoadDonationPage} />
            <Route path="/subscription-success" Component={LoadSubscriptionSuccessPage} />
            <Route path="/subscription-canceled" Component={LoadSubscriptionCanceledPage} />
            <Route path="/search-results" Component={LoadSearchResultsPage} />

            {/* Curated Lists Public Routes - NEW PATTERN */}
            <Route
              path="/curated-lists/browse"
              Component={LoadBrowseAllListsPage}
            />
            <Route
              path="/curated-lists"
              Component={LoadBrowseCuratedListPage}
            />
            <Route
              path="/user/:username/curated-lists/:slug"
              Component={LoadViewCuratedListPage}
            />
     
            {/* Clearing routes */}
            <Route path="/clearing/:slug" Component={LoadClearingPage} />
            <Route path="/clearing/:slug/all" Component={LoadClearingDumpPage} />
            <Route
              path="/clearing/:blog_slug/:blog_id/discussions/:discussion_id"
              Component={LoadDiscussionDetailsPage}
            />

            {/* Blog/Article Public Routes */}

            <Route path="/blog/:blog_slug/:blog_id" Component={LoadBlogPage} />
            <Route
              path="/blog/:blog_slug/:blog_id/discussions/:discussion_id"
              Component={LoadDiscussionDetailsPage}
            />
            <Route
              path="/article/:blog_slug/:blog_id/discussions/:discussion_id"
              Component={LoadDiscussionDetailsPage}
            />
            <Route
              path="/article/:blog_slug/:article_id"
              Component={LoadFeaturedArticlePage}
            />

            <Route path="/blogs/:list_slug" Component={LoadSeeAllBlogsPage} />
            <Route
              path="/articles/:list_slug"
              Component={LoadSeeAllBlogsPage}
            />

            {/* Public Profile */}
            <Route path="/user/:username" Component={LoadPublicProfilePage} />

            {/* Reset Password */}
            <Route
              path="/password-reset/:uid/:token"
              Component={ResetPasswordPage}
            />

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Layout>
    </>
  );
};

export default App;
